﻿import cookieApi from './utils/cookie.js';
import { readConsentState } from './utils/consent.js';
import { apiUrl } from './api/config.js';

const TELEMETRY_ENDPOINT = apiUrl('/public/v1/telemetry');
const GEO_CONTEXT_ENDPOINT = apiUrl('/geo/context');
const GEO_CONTEXT_TIMEOUT_MS = 1200;

const root = typeof document !== 'undefined' ? document.documentElement : null;
const doc = typeof document !== 'undefined' ? document : null;

const telemetryState = {
  endpoint: TELEMETRY_ENDPOINT,
  basePayload: null,
  enabled: false,
  postId: '',
  jobId: '',
  pageCategory: '',
  pageId: '',
};

const FORWARDED_METRIC_EVENTS = new Set(['page_view', 'page_leave', 'fcp', 'lcp']);

function resolveEnabledFlag() {
  if (!root?.dataset) {
    return false;
  }

  const raw = root.dataset.analyticsEnabled;
  if (raw == null) {
    return true;
  }

  const normalized = String(raw).toLowerCase();
  return normalized !== 'false' && normalized !== '0' && normalized !== 'off';
}

function resolvePostId() {
  if (!doc) {
    return '';
  }

  const contentNode = doc.querySelector('[data-content-id]');
  if (contentNode?.dataset?.contentId) {
    return contentNode.dataset.contentId;
  }

  const jobNode = doc.querySelector('[data-job-id]');
  if (jobNode?.dataset?.jobId) {
    return jobNode.dataset.jobId;
  }

  return '';
}

function resolveJobId() {
  if (!doc) {
    return '';
  }

  const jobNode = doc.querySelector('[data-job-id]');
  if (jobNode?.dataset?.jobId) {
    return jobNode.dataset.jobId;
  }

  return '';
}

function resolvePageCategory() {
  if (!doc) {
    return '';
  }

  const htmlCategory = root?.dataset?.category || '';
  const bodyCategory = doc.body?.dataset?.category || '';
  const metaCategory =
    doc.querySelector('meta[name="page-category"]')?.getAttribute('content') ||
    doc.querySelector('meta[name="category"]')?.getAttribute('content') ||
    '';

  return String(htmlCategory || bodyCategory || metaCategory || '').trim().toLowerCase();
}

function resolvePageId() {
  if (!doc) {
    return '';
  }

  const htmlPageId = root?.dataset?.pageId || '';
  const bodyPageId = doc.body?.dataset?.pageId || '';
  const nodePageId = doc.querySelector('[data-page-id]')?.dataset?.pageId || '';
  const urlPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';

  return String(htmlPageId || bodyPageId || nodePageId || urlPath || '').trim();
}

function buildBasePayload() {
  const lang = root?.getAttribute('lang') || '';
  const theme = root?.dataset?.theme || '';
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const search = typeof window !== 'undefined' ? window.location.search || '' : '';
  const params = new URLSearchParams(search);

  return {
    url,
    referrer: doc?.referrer || '',
    sessionId: cookieApi.getSessionId?.() || '',
    userId: cookieApi.getUserId?.() || '',
    lang,
    theme,
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
    utmContent: params.get('utm_content') || '',
    utmTerm: params.get('utm_term') || '',
    ip: '',
    country: '',
    region: '',
    city: '',
    userAgent,
  };
}

function normalizeGeoValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function createTimeoutSignal(timeoutMs) {
  if (typeof AbortController === 'undefined') {
    return { signal: undefined, cancel: () => {} };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    cancel: () => clearTimeout(timeoutId),
  };
}

async function enrichBasePayloadWithGeoContext() {
  if (!telemetryState.basePayload || typeof fetch !== 'function') {
    return false;
  }

  const timeout = createTimeoutSignal(GEO_CONTEXT_TIMEOUT_MS);
  try {
    const response = await fetch(GEO_CONTEXT_ENDPOINT, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cache: 'no-store',
      signal: timeout.signal,
    });

    if (!response.ok) {
      return false;
    }

    const payload = await response.json();
    const ip = normalizeGeoValue(payload?.ip);
    const country = normalizeGeoValue(payload?.country || payload?.['cf-country']);
    const city = normalizeGeoValue(payload?.city);
    const culture = normalizeGeoValue(payload?.culture);

    telemetryState.basePayload = {
      ...telemetryState.basePayload,
      ip: ip || telemetryState.basePayload.ip,
      country: country || telemetryState.basePayload.country,
      city: city || telemetryState.basePayload.city,
      culture: culture || telemetryState.basePayload.culture || '',
    };
    return true;
  } catch {
    return false;
  } finally {
    timeout.cancel();
  }
}

function buildRuntimePayload() {
  return {
    url: typeof window !== 'undefined' ? window.location.href : '',
    sessionId: cookieApi.getSessionId?.() || '',
    userId: cookieApi.getUserId?.() || '',
    cookieConsent: readConsentState(),
  };
}

function withPostId(data, postId) {
  if (!postId) {
    return data || {};
  }

  return {
    postId,
    ...(data || {}),
  };
}

function buildPageViewData() {
  if (telemetryState.pageCategory === 'job' && telemetryState.jobId) {
    return { jobId: telemetryState.jobId };
  }

  if (telemetryState.pageCategory === 'post' && telemetryState.postId) {
    return { postId: telemetryState.postId };
  }

  if (telemetryState.pageId) {
    return { pageId: telemetryState.pageId };
  }

  return {};
}

function sendEvent(payload, { useBeacon = false } = {}) {
  if (!telemetryState.enabled || !telemetryState.endpoint) {
    return false;
  }

  const body = JSON.stringify(payload);
  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' });
    return navigator.sendBeacon(telemetryState.endpoint, blob);
  }

  if (typeof fetch !== 'function') {
    return false;
  }

  fetch(telemetryState.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
    keepalive: useBeacon,
  }).catch(() => {});

  return true;
}

function normalizeMetricValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  return Math.round(numeric);
}

function buildMetricContext(data = {}) {
  return {
    pageId: String(data?.pageId || telemetryState.pageId || ''),
    postId: String(data?.postId || telemetryState.postId || ''),
    jobId: String(data?.jobId || telemetryState.jobId || ''),
    path: typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
    href: typeof window !== 'undefined' ? window.location.href || '' : '',
    title: doc?.title || '',
  };
}

function forwardGoogleAnalyticsMetric({ name, value, data, useBeacon }) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  const metricValue = normalizeMetricValue(value);
  const context = buildMetricContext(data);
  const params = {
    event_source: 'telemetry',
    page_path: context.path,
    page_location: context.href,
    page_title: context.title,
    page_id: context.pageId,
    post_id: context.postId,
    job_id: context.jobId,
  };

  if (name === 'page_leave') {
    params.transport_type = useBeacon ? 'beacon' : 'xhr';
  }

  if (metricValue != null && (name === 'fcp' || name === 'lcp')) {
    params.value = metricValue;
    params.metric_value_ms = metricValue;
    params.metric_unit = 'ms';
  }

  window.gtag('event', name, params);
}

function forwardMetaMetric({ name, value, data }) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }

  if (name !== 'fcp' && name !== 'lcp') {
    return;
  }

  const metricValue = normalizeMetricValue(value);
  const context = buildMetricContext(data);

  window.fbq('trackCustom', name.toUpperCase(), {
    metric: name,
    value: metricValue != null ? metricValue : String(value || ''),
    unit: 'ms',
    path: context.path,
    href: context.href,
    pageId: context.pageId,
    postId: context.postId,
    jobId: context.jobId,
  });
}

function forwardMetricEvents({ name, value, data, useBeacon }) {
  if (!FORWARDED_METRIC_EVENTS.has(name)) {
    return;
  }

  forwardGoogleAnalyticsMetric({ name, value, data, useBeacon });
  forwardMetaMetric({ name, value, data });
}

function trackEvent({ type, name, value = '', data = {}, useBeacon = false, includePostId = true }) {
  if (!telemetryState.basePayload) {
    return false;
  }

  const runtimePayload = buildRuntimePayload();
  const eventData = includePostId ? withPostId(data, telemetryState.postId) : data || {};

  if (telemetryState.enabled) {
    forwardMetricEvents({
      name,
      value,
      data: eventData,
      useBeacon,
    });
  }

  return sendEvent(
    {
      ...telemetryState.basePayload,
      ...runtimePayload,
      eventType: type,
      eventName: name,
      eventValue: String(value || ''),
      eventData,
    },
    { useBeacon }
  );
}

function initViewTracking() {
  trackEvent({
    type: 'view',
    name: 'page_view',
    value: '',
    data: buildPageViewData(),
    includePostId: false,
  });
}

function initLeaveTracking() {
  if (!doc || typeof window === 'undefined') {
    return;
  }

  let leaveSent = false;
  const sendLeave = () => {
    if (leaveSent) {
      return;
    }
    leaveSent = true;
    trackEvent({
      type: 'leave',
      name: 'page_leave',
      value: '',
      data: {},
      useBeacon: true,
    });
  };

  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') {
      sendLeave();
    }
  });
  window.addEventListener('pagehide', sendLeave);
  window.addEventListener('beforeunload', sendLeave);
}

function initScrollTracking() {
  if (typeof window === 'undefined' || !doc) {
    return;
  }

  const thresholds = [20, 40, 60, 80, 100];
  const fired = new Set();
  let ticking = false;

  const onScroll = () => {
    if (ticking) {
      return;
    }

    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;

      const scrollTop = window.scrollY || doc.documentElement.scrollTop || 0;
      const docHeight = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
      const viewportHeight = window.innerHeight || 1;
      const total = Math.max(1, docHeight - viewportHeight);
      const percent = Math.min(100, Math.round((scrollTop / total) * 100));

      thresholds.forEach((threshold) => {
        if (percent < threshold || fired.has(threshold)) {
          return;
        }

        fired.add(threshold);
        trackEvent({
          type: 'scroll',
          name: 'scroll_depth',
          value: String(threshold),
          data: { percent: threshold },
        });
      });
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function initVitalsTracking() {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return;
  }

  const reportVital = (name, value, data = {}) => {
    if (!name) {
      return;
    }

    const normalizedValue =
      typeof value === 'number'
        ? name === 'CLS'
          ? value.toFixed(3)
          : String(Math.round(value))
        : String(value || '');

    trackEvent({
      type: 'vital',
      name: String(name).toLowerCase(),
      value: normalizedValue,
      data,
    });
  };

  const paintObserver = new PerformanceObserver((list) => {
    const entry = list.getEntries().find((item) => item.name === 'first-contentful-paint');
    if (!entry) {
      return;
    }

    reportVital('fcp', entry.startTime, { entryType: entry.entryType });
    paintObserver.disconnect();
  });
  try {
    paintObserver.observe({ type: 'paint', buffered: true });
  } catch {
    return;
  }

  let lcpEntry = null;
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    if (entries.length > 0) {
      lcpEntry = entries[entries.length - 1];
    }
  });
  try {
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    return;
  }

  let lcpReported = false;
  const finalizeLcp = () => {
    if (lcpReported) {
      return;
    }
    lcpReported = true;

    if (lcpEntry) {
      reportVital('lcp', lcpEntry.startTime, { entryType: lcpEntry.entryType });
    }
    lcpObserver.disconnect();
  };

  window.addEventListener('pagehide', finalizeLcp, { once: true });
  doc?.addEventListener(
    'visibilitychange',
    () => {
      if (doc.visibilityState === 'hidden') {
        finalizeLcp();
      }
    },
    { once: true }
  );
}

function initContentLinkTracking() {
  if (!doc || typeof window === 'undefined') {
    return;
  }

  const linkEventState = { href: '', time: 0 };

  const shouldSendLinkEvent = (href) => {
    const now = Date.now();
    if (linkEventState.href === href && now - linkEventState.time < 500) {
      return false;
    }

    linkEventState.href = href;
    linkEventState.time = now;
    return true;
  };

  const handleLinkEvent = (event, trigger) => {
    const link = event.target?.closest?.('a[href]');
    if (!link) {
      return;
    }

    const container = link.closest('.post-body, .page, .collection-content, .external-content, article');
    if (!container) {
      return;
    }

    if (link.dataset?.metric != null || link.dataset?.metrics != null) {
      return;
    }

    const href = link.getAttribute('href') || '';
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return;
    }

    let linkUrl;
    try {
      linkUrl = new URL(href, window.location.href);
    } catch {
      return;
    }

    if (linkUrl.origin === window.location.origin) {
      return;
    }

    if (!shouldSendLinkEvent(linkUrl.href)) {
      return;
    }

    trackEvent({
      type: 'custom',
      name: 'external_link_click',
      value: '',
      data: {
        source: 'content',
        href: linkUrl.href,
        host: linkUrl.host,
        path: linkUrl.pathname,
        target: link.getAttribute('target') || '',
        text: (link.textContent || '').trim().slice(0, 120),
        trigger,
      },
      useBeacon: true,
    });
  };

  doc.addEventListener('click', (event) => handleLinkEvent(event, 'click'), { capture: true });
  doc.addEventListener('auxclick', (event) => handleLinkEvent(event, 'auxclick'), { capture: true });
  doc.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      handleLinkEvent(event, 'keydown');
    },
    { capture: true }
  );
}

function readMetricMeta(dataset) {
  const meta = {};

  Object.entries(dataset || {}).forEach(([key, value]) => {
    if (!key.startsWith('metricMeta')) {
      return;
    }

    const normalizedKey = key.slice('metricMeta'.length);
    if (!normalizedKey) {
      return;
    }

    const metaKey = normalizedKey.charAt(0).toLowerCase() + normalizedKey.slice(1);
    meta[metaKey] = value;
  });

  return meta;
}

function buildMetricPayloadFromElement(element) {
  if (!element) {
    return null;
  }

  const name = element.dataset?.metricName || element.dataset?.metrics || '';
  if (!name) {
    return null;
  }

  const type = element.dataset?.metricType || 'custom';
  const source = element.dataset?.metricSource || '';
  const meta = readMetricMeta(element.dataset);
  const value = element.dataset?.metricValue || (name === 'share_click' ? meta.channel || '' : '');
  const tag = element.tagName ? element.tagName.toLowerCase() : '';

  return {
    type,
    name,
    value,
    data: {
      source,
      meta,
      tag,
      id: element.id || '',
      text: (element.textContent || '').trim().slice(0, 120),
      href: element.tagName === 'A' ? element.getAttribute('href') || '' : '',
      target: element.tagName === 'A' ? element.getAttribute('target') || '' : '',
    },
  };
}

function trackMetricElement(element) {
  const metricPayload = buildMetricPayloadFromElement(element);
  if (!metricPayload) {
    return false;
  }

  return trackEvent(metricPayload);
}

function bindMetricTracking() {
  if (!doc) {
    return;
  }

  doc.addEventListener('click', (event) => {
    const target = event.target?.closest?.('[data-metric], [data-metrics]');
    if (!target) {
      return;
    }

    trackMetricElement(target);
  });
}

async function initTelemetry() {
  if (!resolveEnabledFlag()) {
    return telemetryApi;
  }

  telemetryState.basePayload = buildBasePayload();
  telemetryState.postId = resolvePostId();
  telemetryState.jobId = resolveJobId();
  telemetryState.pageCategory = resolvePageCategory();
  telemetryState.pageId = resolvePageId();
  telemetryState.enabled = true;
  await enrichBasePayloadWithGeoContext();

  initViewTracking();
  initScrollTracking();
  initVitalsTracking();
  initContentLinkTracking();
  // Intentionally not bound by default: call telemetryApi.bindMetricTracking() when needed.
  initLeaveTracking();

  return telemetryApi;
}

const telemetryApi = {
  init: initTelemetry,
  setEnabled: (enabled) => {
    telemetryState.enabled = Boolean(enabled) && Boolean(telemetryState.basePayload);
    return telemetryState.enabled;
  },
  trackCustom: (name, data = {}, value = '') => {
    if (!name) {
      return false;
    }

    return trackEvent({
      type: 'custom',
      name,
      value,
      data,
    });
  },
  trackMetricElement,
  bindMetricTracking,
};

export default telemetryApi;
